import React, { useEffect, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { useAppSelector, useAppDispatch } from '@/hooks/redux'
import { logout } from '@/store/slices/authSlice'
import { apiUrl } from '@/utils/api'

interface InterviewAuthGuardProps {
  children: React.ReactNode
}

export const InterviewAuthGuard: React.FC<InterviewAuthGuardProps> = ({ children }) => {
  const { isAuthenticated, candidateId, token } = useAppSelector(state => state.auth)
  const dispatch = useAppDispatch()
  const [isValidating, setIsValidating] = useState(true)
  const [isValid, setIsValid] = useState(false)

  useEffect(() => {
    const validateSession = async () => {
      // First check if basic auth info is present
      if (!isAuthenticated || !candidateId || !token) {
        console.log('No authentication data found, redirecting to welcome')
        setIsValidating(false)
        setIsValid(false)
        return
      }

      try {
        // Check if there's an active interview session on the backend
        const sessionId = localStorage.getItem('interview-session-id')
        if (!sessionId) {
          console.log('No interview session ID found, redirecting to welcome')
          dispatch(logout())
          setIsValidating(false)
          setIsValid(false)
          return
        }

        // Validate the session with the backend
        const response = await fetch(apiUrl(`/api/v1/interview/sessions/${sessionId}/validate`), {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        })

        if (response.ok) {
          const sessionData = await response.json()
          
          // Check if session is valid (pending, active, but not completed)
          if (sessionData.status === 'active' || sessionData.status === 'pending') {
            console.log(`Valid ${sessionData.status} session found`)
            setIsValid(true)
          } else if (sessionData.status === 'completed') {
            console.log('Session is completed, redirecting to completion page')
            setIsValid(false)
            // Navigate to completion page instead of welcome if interview was completed
            window.location.href = '/completed'
            return
          } else {
            console.log(`Session status '${sessionData.status}' is not valid, redirecting to welcome`)
            dispatch(logout())
            localStorage.removeItem('interview-session-id')
            setIsValid(false)
          }
        } else {
          console.log('Session validation failed, redirecting to welcome')
          dispatch(logout())
          localStorage.removeItem('interview-session-id')
          setIsValid(false)
        }
      } catch (error) {
        console.error('Error validating session:', error)
        dispatch(logout())
        localStorage.removeItem('interview-session-id')
        setIsValid(false)
      }

      setIsValidating(false)
    }

    validateSession()
  }, [isAuthenticated, candidateId, token, dispatch])

  // Show loading while validating
  if (isValidating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 via-white to-snwolley-green-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Validating interview session...</p>
        </div>
      </div>
    )
  }

  // Redirect if not valid
  if (!isValid) {
    return <Navigate to="/welcome" replace />
  }

  // Render children if valid
  return <>{children}</>
}